import {Component, OnDestroy, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {DecimalPipe, NgClass, NgForOf, NgIf} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Subscription, combineLatest, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {EvaluationGame, EvaluationSeason, STATE_LABELS} from '../../model/evaluation/evaluation';
import {hasRole} from '../../model/user';
import {AuthService} from '../../services/auth.service';
import {EvaluationsService} from '../../services/evaluations.service';
import {EvaluationExportService} from '../export/evaluation-export.service';
import {approvedGames} from '../export/export-model';
import {computeReward} from '../reward';

interface ProducerRow {
  game: EvaluationGame;
  total: number;
  waiting: boolean;
}

/**
 * What a production company sees: its own broadcasts, what each one pays, and
 * which ones are waiting for an answer.
 *
 * Games an admin has not released are deliberately absent — a "not met" that
 * flips back a week later would be an argument with a customer instead of a
 * service.
 */
@Component({
  selector: 'app-evaluation-producer-games',
  templateUrl: './producer-games.component.html',
  styleUrls: ['./producer-games.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    DecimalPipe,
    NgClass,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationProducerGamesComponent implements OnInit, OnDestroy {

  readonly stateLabels = STATE_LABELS;

  season: EvaluationSeason;
  rows: ProducerRow[] = [];
  waiting = 0;
  approvedTotal = 0;
  currency = 'CZK';
  noChannel = false;
  /** How many of the rows below are settled, i.e. what the workbook would hold. */
  approved = 0;
  exporting = false;
  exportError = '';

  private channelId: number | null = null;
  private games: EvaluationGame[] = [];
  private subscriptions = new Subscription();

  constructor(
    private auth: AuthService,
    private evaluations: EvaluationsService,
    private exports: EvaluationExportService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.auth.user$.pipe(
        switchMap(user => {
          this.noChannel = hasRole(user, 'producer') && !user.channelId;
          this.channelId = user?.channelId ?? null;
          return user?.channelId
            ? combineLatest([this.evaluations.season(), this.evaluations.producerGames(user.channelId)])
            : of([null, []] as [EvaluationSeason, EvaluationGame[]]);
        })
      ).subscribe(([season, games]) => {
        this.season = season;
        this.currency = season?.currency ?? 'CZK';
        this.games = games ?? [];
        this.approved = this.channelId === null
          ? 0
          : approvedGames(this.games, this.channelId).length;
        this.rows = (games ?? []).map(game => ({
          game,
          total: game.reward?.total ?? (season ? computeReward(game, season).total : 0),
          waiting: game.state === 'toApprove' || game.state === 'returned',
        }));
        this.waiting = this.rows.filter(row => row.waiting).length;
        this.approvedTotal = this.rows
          .filter(row => row.game.state === 'approved')
          .reduce((sum, row) => sum + row.total, 0);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * The production's own copy of its agreed season.
   *
   * The same workbook an admin downloads, built from the same code — on
   * purpose. The point of the file is to settle an argument about an invoice,
   * and an argument is not settled by two sides holding documents that might
   * differ. A producer can only ever read their own channel anyway, so there
   * is nothing here the rules would not already hand them.
   *
   * Approved broadcasts only, which on this screen is worth saying out loud:
   * the table above also lists what is still with them, and those are the ones
   * whose amount can still move.
   */
  async exportSeason(): Promise<void> {
    if (this.channelId === null) {
      return;
    }
    this.exporting = true;
    this.exportError = '';
    try {
      await this.exports.exportProducer(this.channelId, this.games, this.season);
    } catch (error: any) {
      this.exportError = error?.message ?? 'The export failed.';
    } finally {
      this.exporting = false;
    }
  }
}
